import {
  CALORIMETRY_CASES,
  SOLUTION_DENSITY_G_PER_ML,
  WATER_SPECIFIC_HEAT_J_PER_G_K,
  formatDecimalSigned,
  formatSig,
  formatSigned,
  type EnergyDirection,
} from './energyCalorimetry';
import {
  type ExploreChallenge,
  type ModelField,
  type WorkedStep,
  firstModelError,
  formatModelNumber,
} from './modelInputContract';

/**
 * Learner-controlled coffee-cup calorimetry (chemistry/CLAUDE.md "Interactive calculation
 * standard"). The learner types the solution volume, the two thermometer readings and the amount
 * that reacted; the model works out m = V × ρ, ΔT, q = m × c × ΔT and ΔH = −q ÷ n with the same
 * reviewed constants as the fixed Practice cases (c = 4.18 J/(g·°C), ρ = 1.00 g/mL).
 */
export interface CalorimetrySetup {
  volumeMl: number;
  initialC: number;
  finalC: number;
  amountMol: number;
}

export type CalorimetryInput = keyof CalorimetrySetup;

export const CALORIMETRY_FIELDS: ModelField<CalorimetryInput>[] = [
  { key: 'volumeMl', label: 'Solution volume', unit: 'mL', min: 10, max: 500, step: 1 },
  { key: 'initialC', label: 'Initial temperature', unit: '°C', min: 0, max: 100, step: 0.1 },
  { key: 'finalC', label: 'Final temperature', unit: '°C', min: 0, max: 100, step: 0.1 },
  { key: 'amountMol', label: 'Amount reacted', unit: 'mol', min: 0.0001, max: 5, step: 0.0001 },
];

/** Largest solution the cup in the 3D view holds; the liquid level follows the volume up to this. */
export const CUP_CAPACITY_ML = 500;
/** Liquid-water range of the model: at 0 °C or 100 °C the water may freeze or boil. */
export const LIQUID_RANGE_C = { min: 0, max: 100 } as const;
/**
 * Dilute-solution boundary for ρ = 1.00 g/mL and c = 4.18 J/(g·°C). Source: Airbotix teaching
 * model boundary (MODEL_BOUNDARIES "Solutions"); the reviewed Practice cases reach 1.25 mol/L, so
 * the model stops at 2.0 mol/L. reviewStatus: 'pending-teaching-review'.
 */
export const DILUTE_LIMIT_MOL_PER_L = 2.0;
/** Illustrative room temperature used when a preset only gives ΔT (as in the Evidence cases). */
export const PRESET_START_C = 21.0;

const JOULES_PER_KILOJOULE = 1000;
const MILLILITRES_PER_LITRE = 1000;

export const CALORIMETRY_ASSUMPTIONS =
  'Model assumptions: density 1.00 g/mL, c = 4.18 J/(g·°C) for the solution, and no heat lost to or gained from the cup, lid or air.';

export type CalorimetryLimit = 'phase-change' | 'concentrated';

export interface CalorimetryExploreResult {
  massG: number;
  deltaTC: number;
  /** Null when the model stops before q (phase-change limit). */
  qJ: number | null;
  qkJ: number | null;
  /** Null when there is no temperature change or the model stops at a limit. */
  deltaHkJPerMol: number | null;
  direction: EnergyDirection | null;
  concentrationMolPerL: number;
  limit: CalorimetryLimit | null;
  steps: WorkedStep[];
  result: string;
  /** Always present: the model assumptions, led by the limit message when the model stops. */
  limitNote: string;
}

function signed(value: number, digits: number): string {
  if (value === 0) return '0';
  return `${value > 0 ? '+' : '−'}${formatModelNumber(Math.abs(value), digits)}`;
}

function temperatureText(value: number): string {
  return `${value.toFixed(1)} °C`;
}

/** Worked calorimetry for one learner setup. Throws on invalid input (fail closed). */
export function calculateCalorimetryExplore(setup: CalorimetrySetup): CalorimetryExploreResult {
  const error = firstModelError(CALORIMETRY_FIELDS, setup);
  if (error) throw new Error(error);
  const { volumeMl, initialC, finalC, amountMol } = setup;

  const massG = volumeMl * SOLUTION_DENSITY_G_PER_ML;
  const deltaTC = finalC - initialC;
  const concentrationMolPerL = amountMol / (volumeMl / MILLILITRES_PER_LITRE);
  const atPhaseChange = [initialC, finalC].some((t) => t <= LIQUID_RANGE_C.min || t >= LIQUID_RANGE_C.max);
  const limit: CalorimetryLimit | null = atPhaseChange
    ? 'phase-change'
    : concentrationMolPerL > DILUTE_LIMIT_MOL_PER_L
      ? 'concentrated'
      : null;

  const qJ = limit === 'phase-change' ? null : massG * WATER_SPECIFIC_HEAT_J_PER_G_K * deltaTC;
  const qkJ = qJ === null ? null : qJ / JOULES_PER_KILOJOULE;
  const canFindDeltaH = qkJ !== null && limit === null && deltaTC !== 0;
  const deltaHkJPerMol = canFindDeltaH ? -qkJ / amountMol : null;
  const direction: EnergyDirection | null = deltaHkJPerMol === null ? null : deltaHkJPerMol < 0 ? 'exothermic' : 'endothermic';

  const steps: WorkedStep[] = [
    {
      id: 'mass',
      title: 'Mass of solution',
      formula: 'm = V × ρ',
      working: `m = ${formatModelNumber(volumeMl, 1)} mL × ${SOLUTION_DENSITY_G_PER_ML.toFixed(2)} g/mL = ${formatModelNumber(massG, 1)} g`,
      status: 'done',
    },
    {
      id: 'delta-t',
      title: 'Temperature change',
      formula: 'ΔT = T(final) − T(initial)',
      working: `ΔT = ${temperatureText(finalC)} − ${temperatureText(initialC)} = ${formatDecimalSigned(deltaTC, 1)} °C`,
      status: 'done',
      note: deltaTC > 0 ? 'solution warmed' : deltaTC < 0 ? 'solution cooled' : 'no change',
    },
    {
      id: 'heat',
      title: 'Energy gained by the solution',
      formula: 'q = m × c × ΔT',
      working:
        qJ === null || qkJ === null
          ? ''
          : `q = ${formatModelNumber(massG, 1)} g × ${WATER_SPECIFIC_HEAT_J_PER_G_K} J/(g·°C) × ${formatDecimalSigned(deltaTC, 1)} °C = ${signed(qJ, 0)} J = ${formatSigned(qkJ, 3)} kJ`,
      status: qJ === null ? 'not-reached' : 'done',
      note: qJ === null ? 'stopped: water may freeze or boil' : undefined,
    },
    {
      id: 'enthalpy',
      title: 'Enthalpy change per mole',
      formula: 'ΔH = −q ÷ n',
      working:
        deltaHkJPerMol === null || qkJ === null
          ? ''
          : `ΔH = −(${formatSigned(qkJ, 3)} kJ) ÷ ${formatSig(amountMol, 3)} mol = ${formatSigned(deltaHkJPerMol, 3)} kJ/mol`,
      status: deltaHkJPerMol === null ? 'not-reached' : 'done',
      note:
        direction ??
        (limit === 'phase-change'
          ? 'needs q'
          : limit === 'concentrated'
            ? 'stopped: solution too concentrated'
            : 'needs a temperature change'),
    },
  ];

  let result: string;
  let limitMessage = '';
  if (limit === 'phase-change') {
    result = `Stopped at ΔT = ${formatDecimalSigned(deltaTC, 1)} °C · the model cannot work out q at 0 °C or 100 °C`;
    limitMessage =
      'At 0 °C the water may freeze and at 100 °C it may boil, so q = m × c × ΔT no longer covers all the energy. The model stops here instead of guessing. ';
  } else if (limit === 'concentrated') {
    result = `q = ${formatSigned(qkJ ?? 0, 3)} kJ · ΔH not worked out (${formatSig(concentrationMolPerL, 3)} mol/L is above ${DILUTE_LIMIT_MOL_PER_L.toFixed(1)} mol/L)`;
    limitMessage = `${formatSig(amountMol, 3)} mol in ${formatModelNumber(volumeMl, 1)} mL is ${formatSig(concentrationMolPerL, 3)} mol/L. Above ${DILUTE_LIMIT_MOL_PER_L.toFixed(1)} mol/L the density and heat capacity of water no longer fit the solution, so the model stops before ΔH. `;
  } else if (deltaHkJPerMol === null) {
    result = 'No temperature change, so q = 0 and the model cannot name an energy direction';
  } else {
    const flow = deltaHkJPerMol < 0 ? 'released to' : 'taken in from';
    result = `ΔH = ${formatSigned(deltaHkJPerMol, 3)} kJ/mol · ${direction}: ${formatSig(Math.abs(qkJ ?? 0), 3)} kJ ${flow} the solution`;
  }

  return {
    massG,
    deltaTC,
    qJ,
    qkJ,
    deltaHkJPerMol,
    direction,
    concentrationMolPerL,
    limit,
    steps,
    result,
    limitNote: `${limitMessage}${CALORIMETRY_ASSUMPTIONS}`,
  };
}

export interface CalorimetryPreset {
  id: string;
  name: string;
  setup: CalorimetrySetup;
  referenceDeltaHkJ: number;
  perMolOf: string;
}

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * Shortcuts from the reviewed coffee-cup Practice cases. A case only gives ΔT, so the preset
 * starts at an illustrative room temperature; n is rounded to the input step.
 */
export const CALORIMETRY_PRESETS: CalorimetryPreset[] = CALORIMETRY_CASES.filter((entry) => entry.id.startsWith('cup-')).map((entry) => ({
  id: entry.id,
  name: entry.name,
  setup: {
    volumeMl: entry.liquidVolumeMl,
    initialC: PRESET_START_C,
    finalC: roundTo(PRESET_START_C + entry.temperatureChangeK, 1),
    amountMol: roundTo(entry.amountMol, 4),
  },
  referenceDeltaHkJ: entry.referenceDeltaHkJ,
  perMolOf: entry.perMolOf,
}));

export const DEFAULT_CALORIMETRY_SETUP: CalorimetrySetup = CALORIMETRY_PRESETS[0].setup;

export const CALORIMETRY_QUESTION =
  'How can a thermometer reading tell you how much energy a reaction gives out or takes in, for each mole?';

/** Temperature rise of the default HCl + NaOH setup (21.0 °C → 27.6 °C). */
const DEFAULT_RISE_C = 6.6;
/** q of the default setup: 100 g × 4.18 J/(g·°C) × 6.6 °C = 2.7588 kJ, so double is 5.5176 kJ. */
const DOUBLE_Q_KJ = { min: 5.2, max: 5.8 } as const;
/** ΔH per mole of water formed for the default setup is −55.2 kJ/mol; ± 5 % band. */
const NEUTRALISATION_DELTA_H_KJ = { min: -58, max: -52 } as const;
const SAME_RISE_TOLERANCE_C = 0.05;

/** Teaching tasks (CT-INTERACT C7). Each check reads only the calorimetry result, never the UI. */
export const CALORIMETRY_CHALLENGES: ExploreChallenge<CalorimetryExploreResult>[] = [
  {
    id: 'positive-delta-h',
    prompt: 'Make the solution cool down, then look at the sign of ΔH.',
    hint: 'Set the final temperature below the initial temperature, or try a dissolving preset that gets colder.',
    isMet: (result) => result.direction === 'endothermic' && result.deltaTC < 0,
    why: 'When the solution cools, it has lost energy (q is negative) to the reacting chemicals, so ΔH = −q ÷ n is positive: the change is endothermic. A falling thermometer means a positive ΔH, not a negative one.',
  },
  {
    id: 'double-volume',
    prompt: 'Keep the rise at +6.6 °C, but make q twice as big (between 5.2 and 5.8 kJ).',
    hint: 'Keep both temperatures and change only the solution volume.',
    isMet: (result) =>
      result.qkJ !== null &&
      Math.abs(result.deltaTC - DEFAULT_RISE_C) <= SAME_RISE_TOLERANCE_C &&
      result.qkJ >= DOUBLE_Q_KJ.min &&
      result.qkJ <= DOUBLE_Q_KJ.max,
    why: 'The same temperature rise in twice the mass of solution needs twice the energy: 200 g × 4.18 × 6.6 = 5.52 kJ, double the 2.76 kJ for 100 g. The temperature change alone does not tell you the energy.',
  },
  {
    id: 'per-mole',
    prompt: 'Make q at least 5.2 kJ, but keep ΔH at about −55 kJ/mol (between −58 and −52).',
    hint: 'A bigger experiment also has more reacting chemicals. What must happen to the amount reacted?',
    isMet: (result) =>
      result.qkJ !== null &&
      result.qkJ >= DOUBLE_Q_KJ.min &&
      result.deltaHkJPerMol !== null &&
      result.deltaHkJPerMol >= NEUTRALISATION_DELTA_H_KJ.min &&
      result.deltaHkJPerMol <= NEUTRALISATION_DELTA_H_KJ.max,
    why: 'Doubling the solutions doubles both q and the amount reacted, so ΔH = −5.52 kJ ÷ 0.100 mol is still −55.2 kJ/mol. ΔH is per mole, so it does not grow with the size of the experiment.',
  },
];
